import React, { useEffect, useState } from "react";
import { api, fmtDate, fmtNum } from "../lib/api";
import { PageHeader, Surface, StatusTag } from "../components/Primitives";
import { AlertsBanner } from "../components/AlertsBanner";
import { useAuth } from "../contexts/AuthContext";

export default function Compliance() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner" || user?.role === "tenant_admin";
  const [memberships, setMemberships] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/memberships").then((r) => setMemberships(r.data)),
      api.get("/participants").then((r) => setUsers(r.data)),
    ]);
  }, []);

  const userMap = Object.fromEntries(users.map((u) => [u.user_id, u]));
  const grouped = {};
  for (const m of memberships) {
    const key = m.aircraft_registration || m.aircraft_id || "—";
    (grouped[key] = grouped[key] || []).push(m);
  }

  return (
    <div>
      <PageHeader overline="Compliance" title="PIC privileges & recency"
        subtitle="Compliance is tracked per (pilot, aircraft) — license, medical, named-on-insurance, recency 90d & annual instructor flight." />
      <div className="p-8 space-y-6">
        <AlertsBanner scope="me" maxRows={6} dismissable={false} />        {Object.keys(grouped).length === 0 && (
          <Surface className="p-12 text-center text-sm text-zinc-500">
            {isOwner ? "No memberships yet. Add members on each aircraft's detail page." : "You are not a member of any aircraft yet. Contact the Owner."}
          </Surface>
        )}
        {Object.entries(grouped).map(([reg, items]) => (
          <Surface key={reg} data-testid={`compliance-aircraft-${reg}`}>
            <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
              <div className="font-mono text-lg text-amber">{reg}</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">{items.length} member{items.length === 1 ? "" : "s"}</div>
            </div>
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono border-b border-zinc-900">
                <tr>
                  <th className="text-left py-3 px-4">Pilot</th>
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-left py-3 px-4">Medical</th>
                  <th className="text-left py-3 px-4">Insurance</th>
                  <th className="text-left py-3 px-4">Last flight</th>
                  <th className="text-right py-3 px-4">Hrs/type</th>
                  <th className="text-left py-3 px-4">Issues</th>
                </tr>
              </thead>
              <tbody>
                {items.map((m) => {
                  const u = userMap[m.user_id] || {};
                  return (
                    <tr key={m.user_id} className="border-b border-zinc-900">
                      <td className="py-2.5 px-4">{m.user_name}</td>
                      <td className="py-2.5 px-4"><StatusTag status={m.status} /></td>
                      <td className="py-2.5 px-4 font-mono text-zinc-300">{fmtDate(u.medical_expiry)}</td>
                      <td className="py-2.5 px-4">{m.named_on_insurance ? <span className="text-green-400 text-xs">Named</span> : <span className="text-red-400 text-xs">Not named</span>}</td>
                      <td className="py-2.5 px-4 font-mono text-zinc-300">{fmtDate(m.last_flight_date)}</td>
                      <td className="py-2.5 px-4 text-right font-mono">{fmtNum(m.total_hours_on_type, 1)}</td>
                      <td className="py-2.5 px-4 text-[10px] font-mono">{(m.compliance_issues || []).length === 0 ? <span className="text-green-400 uppercase tracking-wider">OK</span> : <span className="text-amber">{m.compliance_issues.join(", ")}</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Surface>
        ))}
      </div>
    </div>
  );
}
